import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiCalendar } from 'react-icons/fi';
import Calling from '../api/Calling';
import NotFound from './ul/NotFound';
import Loader from './Loader';

// Main Button component for date filters
const FilterButton = ({ onClick, label, isSelected }) => {
    return (
        <button
            onClick={onClick}
            className={`px-4 py-2 rounded-lg shadow-sm border ${isSelected
                ? 'border-blue-600 ring-2 ring-blue-200 text-blue-600 bg-blue-50'
                : 'border-gray-300 text-gray-700 hover:border-blue-500 hover:text-blue-600'
                }`}
        >
            {label}
        </button>
    );
};

const AdsDataDisplay = ({ userId }) => {
    const [loading, setLoading] = useState(false);
    const [adsData, setAdsData] = useState(null);
    const [selectedFilter, setSelectedFilter] = useState('today');
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

    // Fetch user's ads click data with date range
    const fetchAdsClickData = async () => {
        if (!userId) return;

        setLoading(true);
        try {
            const response = await Calling.userInfoAdsClick(userId, startDate, endDate);
            if (response?.status) {
                setAdsData(response?.data);
            } else {
                toast.error(response?.message || 'Failed to fetch ads data.');
            }
        } catch (error) {
            console.error('Error fetching ads data:', error);
            toast.error('An unexpected error occurred.');
        } finally {
            setLoading(false);
        }
    };

    // Handle date range filter selection
    const handleDateRangeFilter = (range) => {
        const today = new Date();
        let start, end;

        switch (range) {
            case 'today':
                start = today.toISOString().split('T')[0];
                end = start;
                setSelectedFilter('today');
                break;
            case 'yesterday':
                const yesterday = new Date();
                yesterday.setDate(today.getDate() - 1);
                start = yesterday.toISOString().split('T')[0];
                end = start;
                setSelectedFilter('yesterday');
                break;
            case 'thisWeek':
                const firstDayOfWeek = new Date(today);
                firstDayOfWeek.setDate(today.getDate() - today.getDay());
                const lastDayOfWeek = new Date(firstDayOfWeek);
                lastDayOfWeek.setDate(firstDayOfWeek.getDate() + 6);
                start = firstDayOfWeek.toISOString().split('T')[0];
                end = lastDayOfWeek.toISOString().split('T')[0];
                setSelectedFilter('thisWeek');
                break;
            case 'thisMonth':
                start = new Date(today.getFullYear(), today.getMonth(), 1)
                    .toISOString()
                    .split('T')[0];
                end = new Date(today.getFullYear(), today.getMonth() + 1, 0)
                    .toISOString()
                    .split('T')[0];
                setSelectedFilter('thisMonth');
                break;
            case 'last6Months':
                start = new Date(today.getFullYear(), today.getMonth() - 5, 1)
                    .toISOString()
                    .split('T')[0];
                end = new Date(today.getFullYear(), today.getMonth() + 1, 0)
                    .toISOString()
                    .split('T')[0];
                setSelectedFilter('last6Months');
                break;
            default:
                setSelectedFilter('custom');
        }

        setStartDate(start);
        setEndDate(end);
    };

    // Handle form date changes
    const handleDateChange = (e) => {
        if (e.target.name === "startDate") {
            setStartDate(e.target.value);
        } else {
            setEndDate(e.target.value);
        }
        setSelectedFilter('custom');
    };

    // Handle filter submission
    const handleFilterSubmit = (e) => {
        e.preventDefault();
        fetchAdsClickData();
    };

    // Initial data fetch on component mount
    useEffect(() => {
        fetchAdsClickData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    // Fetch data when date range changes
    useEffect(() => {
        fetchAdsClickData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate]);

    if (loading) {
        return <Loader loader={loading} />;
    }

    return (
        <div className="w-full mt-5">
            {/* Date Range Filters */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
                <div className="p-6">
                    <div className="flex items-center mb-4">
                        <FiCalendar className="text-blue-600 mr-2" />
                        <h2 className="text-lg font-semibold text-gray-800">Date Range</h2>
                    </div>
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-12 md:col-span-8 flex flex-wrap gap-3">
                            <FilterButton
                                onClick={() => handleDateRangeFilter('today')}
                                label="Today"
                                isSelected={selectedFilter === 'today'}
                            />
                            <FilterButton
                                onClick={() => handleDateRangeFilter('yesterday')}
                                label="Yesterday"
                                isSelected={selectedFilter === 'yesterday'}
                            />
                            <FilterButton
                                onClick={() => handleDateRangeFilter('thisWeek')}
                                label="This Week"
                                isSelected={selectedFilter === 'thisWeek'}
                            />
                            <FilterButton
                                onClick={() => handleDateRangeFilter('thisMonth')}
                                label="This Month"
                                isSelected={selectedFilter === 'thisMonth'}
                            />
                            <FilterButton
                                onClick={() => handleDateRangeFilter('last6Months')}
                                label="Last 6 Months"
                                isSelected={selectedFilter === 'last6Months'}
                            />
                        </div>

                        {/* Date Range Form */}
                        <div className="col-span-12 md:col-span-4 flex justify-end">
                            <form onSubmit={handleFilterSubmit} className="w-full md:w-auto">
                                <div className="grid grid-cols-12 gap-3 justify-items-end">
                                    <div className="col-span-6 relative">
                                        <input
                                            type="date"
                                            name="startDate"
                                            value={startDate}
                                            onChange={handleDateChange}
                                            min="2023-01-01"
                                            max={new Date().toISOString().split('T')[0]}
                                            className={`w-full px-4 py-2 rounded-lg shadow-sm border cursor-pointer bg-white ${selectedFilter === 'custom'
                                                ? 'border-blue-600 ring-2 ring-blue-200 text-blue-600'
                                                : 'border-gray-300 text-gray-700 hover:border-blue-500'
                                                }`}
                                        />
                                    </div>
                                    <div className="col-span-6 relative">
                                        <input
                                            type="date"
                                            name="endDate"
                                            value={endDate}
                                            onChange={handleDateChange}
                                            min="2023-01-01"
                                            max={new Date().toISOString().split('T')[0]}
                                            className={`w-full px-4 py-2 rounded-lg shadow-sm border cursor-pointer bg-white ${selectedFilter === 'custom'
                                                ? 'border-blue-600 ring-2 ring-blue-200 text-blue-600'
                                                : 'border-gray-300 text-gray-700 hover:border-blue-500'
                                                }`}
                                        />
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            {/* Ads Data Display */}
            {!adsData ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg shadow-inner">
                    <NotFound />
                    <p className="text-gray-600 mt-4">No ads data available for this time period</p>
                </div>
            ) : (
                <>
                    <h2 className="text-xl font-bold mb-4">Ads Interaction Data</h2>
                    <hr className="border-gray-200 mb-6" />

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                        {/* Facebook Ads */}
                        <div className="bg-white p-4 rounded-lg shadow border border-gray-100">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-md font-medium text-gray-700">Full Screen / Open</h3>
                                <div className="bg-blue-100 text-blue-800 p-1 rounded">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path d="M11 17a1 1 0 001.447.894l4-2A1 1 0 0017 15V9.236a1 1 0 00-1.447-.894l-4 2a1 1 0 00-.553.894V17zM15.211 6.276a1 1 0 000-1.788l-4.764-2.382a1 1 0 00-.894 0L4.789 4.488a1 1 0 000 1.788l4.764 2.382a1 1 0 00.894 0l4.764-2.382zM4.447 8.342A1 1 0 003 9.236V15a1 1 0 00.553.894l4 2A1 1 0 009 17v-5.764a1 1 0 00-.553-.894l-4-2z" />
                                    </svg>
                                </div>
                            </div>
                            <div className="text-2xl font-bold text-blue-600">{adsData.fsad || 0}</div>
                            <p className="text-xs text-gray-500">Total interactions</p>
                        </div>

                        {/* Native Ads */}
                        <div className="bg-white p-4 rounded-lg shadow border border-gray-100">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-md font-medium text-gray-700">Native Ads</h3>
                                <div className="bg-green-100 text-green-800 p-1 rounded">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                                    </svg>
                                </div>
                            </div>
                            <div className="text-2xl font-bold text-green-600">{adsData.ntvad || 0}</div>
                            <p className="text-xs text-gray-500">Total interactions</p>
                        </div>

                        {/* In-House Ads */}
                        <div className="bg-white p-4 rounded-lg shadow border border-gray-100">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-md font-medium text-gray-700">In-House Ads</h3>
                                <div className="bg-purple-100 text-purple-800 p-1 rounded">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z" />
                                    </svg>
                                </div>
                            </div>
                            <div className="text-2xl font-bold text-purple-600">{adsData.inhousad || 0}</div>
                            <p className="text-xs text-gray-500">Total interactions</p>
                        </div>

                        {/* Reward Ads */}
                        <div className="bg-white p-4 rounded-lg shadow border border-gray-100">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-md font-medium text-gray-700">Reward Ads</h3>
                                <div className="bg-yellow-100 text-yellow-800 p-1 rounded">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M5 2a2 2 0 00-2 2v14l3.5-2 3.5 2 3.5-2 3.5 2V4a2 2 0 00-2-2H5zm4.707 3.707a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L8.414 9H10a3 3 0 013 3v1a1 1 0 102 0v-1a5 5 0 00-5-5H8.414l1.293-1.293z" clipRule="evenodd" />
                                    </svg>
                                </div>
                            </div>
                            <div className="text-2xl font-bold text-yellow-600">{adsData.rwdad || 0}</div>
                            <p className="text-xs text-gray-500">Total interactions</p>
                        </div>

                        {/* Total Coins */}
                        <div className="bg-white p-4 rounded-lg shadow border border-gray-100">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-md font-medium text-gray-700">Total Coins</h3>
                                <div className="bg-red-100 text-red-800 p-1 rounded">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                                    </svg>
                                </div>
                            </div>
                            <div className="text-2xl font-bold text-red-600">{adsData.totalCoin || 0}</div>
                            <p className="text-xs text-gray-500">Earned from ads</p>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default AdsDataDisplay;